import { useMemo } from "react";
import { TYPOGRAPHY, SPACING } from '@/design-system';
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

type StrengthLevel = "weak" | "medium" | "strong";

interface StrengthResult {
  level: StrengthLevel;
  score: number;
  feedbackKey: string;
  color: string;
  barColor: string;
}

export const PasswordStrengthIndicator = ({ password, className }: PasswordStrengthIndicatorProps) => {
  const { lCommon } = useLocalizations();
  
  const strength = useMemo((): StrengthResult => {
    if (!password) {
      return {
        level: "weak",
        score: 0,
        feedbackKey: "",
        color: "text-muted-foreground",
        barColor: "bg-muted"
      };
    }

    let score = 0;
    
    // Length check
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;
    
    // Character variety checks
    if (/[a-z]/.test(password)) score += 15; // lowercase
    if (/[A-Z]/.test(password)) score += 15; // uppercase
    if (/[0-9]/.test(password)) score += 15; // numbers
    if (/[^a-zA-Z0-9]/.test(password)) score += 15; // special chars
    
    // Determine strength level
    if (score < 40) {
      return {
        level: "weak",
        score,
        feedbackKey: "validation.password_strength.weak",
        color: "text-destructive",
        barColor: "bg-destructive"
      };
    } else if (score < 70) {
      return {
        level: "medium",
        score,
        feedbackKey: "validation.password_strength.medium",
        color: "text-warning",
        barColor: "bg-warning"
      };
    } else {
      return {
        level: "strong",
        score,
        feedbackKey: "validation.password_strength.strong",
        color: "text-success",
        barColor: "bg-success"
      };
    }
  }, [password]);

  if (!password) return null;

  return (
    <div className={cn(SPACING.listItemGap, className)}>
      <div className="flex gap-1">
        <div className={cn("h-1.5 flex-1 rounded-full transition-colors", 
          strength.score > 0 ? strength.barColor : "bg-muted"
        )} />
        <div className={cn("h-1.5 flex-1 rounded-full transition-colors", 
          strength.score >= 40 ? strength.barColor : "bg-muted"
        )} />
        <div className={cn("h-1.5 flex-1 rounded-full transition-colors", 
          strength.score >= 70 ? strength.barColor : "bg-muted"
        )} />
      </div>
      <p className={cn(TYPOGRAPHY.labelXsPlain, 'transition-colors', strength.color)}>
        {strength.feedbackKey ? lCommon(strength.feedbackKey) : ''}
      </p>
    </div>
  );
};
